/* -- coding: utf-8 -- */
const ELEMENT_NODE = typeof Node !== 'undefined' ? Node.ELEMENT_NODE : 1;
const TEXT_NODE = typeof Node !== 'undefined' ? Node.TEXT_NODE : 3;

const escapeHtml = (str = '') => String(str)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const hasHtmlTag = (value) => /<\/?[a-z][^>]*>/i.test(value || '');

const styleIncludes = (style = '', pattern) => {
  if (!style || !pattern) return false;
  try {
    return pattern.test(style);
  } catch (error) {
    return false;
  }
};

const normalizeWhitespace = (text = '') => {
  if (!text) return '';
  const replaced = text.replace(/\s+/g, ' ');
  const hasLeading = /^\s/.test(replaced);
  const hasTrailing = /\s$/.test(replaced);
  const trimmed = replaced.trim();
  return {
    lead: hasLeading ? ' ' : '',
    trail: hasTrailing ? ' ' : '',
    core: trimmed,
  };
};

export const sanitizeRichText = (input = '') => {
  const value = typeof input === 'string' ? input : '';
  if (!value) return '';

  const allowedTags = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'A', 'UL', 'OL', 'LI', 'P', 'BR', 'DIV', 'SPAN']);
  const temp = typeof document !== 'undefined' ? document.createElement('div') : null;

  if (!temp) {
    // Fallback: strip tags except a minimal whitelist using regex
    return value
      .replace(/<(?!\/?(b|strong|i|em|u|ul|ol|li|br)\b)[^>]*>/gi, '')
      .replace(/\n/g, '<br />');
  }

  temp.innerHTML = value;

  const cleanNode = (node) => {
    const childNodes = Array.from(node.childNodes);
    childNodes.forEach((child) => {
      if (child.nodeType === ELEMENT_NODE) {
        if (child.tagName === 'SPAN') {
          const style = child.getAttribute('style') || '';
          const className = child.getAttribute('class') || '';
          const wrappers = [];

          if (styleIncludes(style, /font-weight\s*:\s*(bold|[6-9]00)/i) || /\bck-?bold\b/i.test(className)) {
            wrappers.push('strong');
          }

          if (styleIncludes(style, /font-style\s*:\s*italic/i) || /\bck-?italic\b/i.test(className)) {
            wrappers.push('em');
          }

          if (
            styleIncludes(style, /text-decoration\s*:\s*underline/i) ||
            /\bck-?underline\b/i.test(className) ||
            /\btext-underline\b/i.test(className)
          ) {
            wrappers.push('u');
          }

          if (wrappers.length) {
            const created = wrappers.map((tag) => document.createElement(tag));
            for (let i = 1; i < created.length; i += 1) {
              created[i - 1].appendChild(created[i]);
            }
            const rootWrapper = created[0];
            const innermost = created[created.length - 1];
            while (child.firstChild) {
              innermost.appendChild(child.firstChild);
            }
            child.replaceWith(rootWrapper);
            cleanNode(rootWrapper);
            return;
          }
        }

        if (!allowedTags.has(child.tagName)) {
          if (child.childNodes.length) {
            const fragment = document.createDocumentFragment();
            while (child.firstChild) {
              fragment.appendChild(child.firstChild);
            }
            child.replaceWith(fragment);
          } else {
            child.remove();
          }
          return;
        }

        if (child.tagName === 'A') {
          const href = child.getAttribute('href');
          if (!href || !/^https?:\/\//i.test(href)) {
            child.replaceWith(document.createTextNode(child.textContent || ''));
            return;
          }
          child.setAttribute('target', '_blank');
          child.setAttribute('rel', 'noopener noreferrer');
        }

        Array.from(child.attributes).forEach((attr) => {
          if (child.tagName === 'A' && ['href', 'target', 'rel'].includes(attr.name)) return;
          child.removeAttribute(attr.name);
        });

        cleanNode(child);
      } else if (child.nodeType === TEXT_NODE) {
        // keep text nodes
      } else {
        child.remove();
      }
    });
  };

  cleanNode(temp);

  // Normalize divs to paragraphs for consistent spacing
  let html = temp.innerHTML;
  html = html.replace(/<div><br\/?><\/div>/gi, '');
  html = html.replace(/<div>/gi, '<p>').replace(/<\/div>/gi, '</p>');
  html = html.replace(/\n/g, '<br />');

  return html.trim();
};

const applyInlineFormatting = (text = '') => {
  if (!text) return '';
  let result = text;
  // eslint-disable-next-line no-useless-escape
  result = result.replace(/(^|[\s(\[{])\*(\S[^*]*?\S)\*(?=[\s.,!?;:)\]}]|$)/g, (match, prefix, inner) => `${prefix}<strong>${inner}</strong>`);
  // eslint-disable-next-line no-useless-escape
  result = result.replace(/(^|[\s(\[{])_(\S[^_]*?\S)_(?=[\s.,!?;:)\]}]|$)/g, (match, prefix, inner) => `${prefix}<em>${inner}</em>`);
  result = result.replace(/(https?:\/\/[^\s<]+)/gi, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
  return result;
};

const linesToListHtml = (lines, type) => {
  if (!lines.length) return '';
  const items = lines.map((line) => `<li>${applyInlineFormatting(escapeHtml(line))}</li>`).join('');
  return `<${type}>${items}</${type}>`;
};

export const whatsappToRichText = (input = '') => {
  const value = typeof input === 'string' ? input : '';
  if (!value) return '';

  if (hasHtmlTag(value)) {
    return sanitizeRichText(value);
  }

  const normalized = value.replace(/\r\n?/g, '\n');
  const rawLines = normalized.split('\n');
  const blocks = [];
  let currentList = null;

  const flushList = () => {
    if (!currentList) return;
    blocks.push(linesToListHtml(currentList.items, currentList.type));
    currentList = null;
  };

  rawLines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      blocks.push('');
      return;
    }

    const bullet = /^(?:•|-)\\s+(.*)$/.exec(trimmed);
    if (bullet) {
      const content = bullet[1];
      if (!currentList || currentList.type !== 'ul') {
        flushList();
        currentList = { type: 'ul', items: [] };
      }
      currentList.items.push(content);
      return;
    }

    const numbered = /^(\d+)[.)]\s+(.*)$/.exec(trimmed);
    if (numbered) {
      const content = numbered[2];
      if (!currentList || currentList.type !== 'ol') {
        flushList();
        currentList = { type: 'ol', items: [] };
      }
      currentList.items.push(content);
      return;
    }

    flushList();
    blocks.push(`<p>${applyInlineFormatting(escapeHtml(trimmed))}</p>`);
  });

  flushList();

  const html = blocks.join('');
  return sanitizeRichText(html || escapeHtml(value));
};

const collectText = (node, context = {}) => {
  if (!node) return '';

  if (node.nodeType === TEXT_NODE) {
    const raw = node.nodeValue.replace(/\u00A0/g, ' ');
    const parts = normalizeWhitespace(raw);
    if (!parts) return '';
    const { lead, trail, core } = parts;
    if (!core && !lead && !trail) return '';
    if (!core) return (lead || trail) ? ' ' : '';
    let value = core;
    if (context.italic) value = `_${value}_`;
    if (context.bold) value = `*${value}*`;
    return `${lead}${value}${trail}`;
  }

  if (node.nodeType !== ELEMENT_NODE) return '';

  const tag = node.tagName;

  if (tag === 'BR') return '\n';

  if (tag === 'A') {
    const href = node.getAttribute('href');
    const inner = Array.from(node.childNodes).map((child) => collectText(child, { ...context, bold: false, italic: false })).join('');
    const text = inner || href || '';
    if (!href) return text;
    if (!text || text === href) return href;
    return `${text} (${href})`;
  }

  const nextContext = {
    ...context,
    bold: context.bold || tag === 'B' || tag === 'STRONG',
    italic: context.italic || tag === 'I' || tag === 'EM' || tag === 'U',
  };

  if (tag === 'UL' || tag === 'OL') {
    let index = 1;
    const items = [];
    Array.from(node.children).forEach((child) => {
      if (child.tagName !== 'LI') return;
      const content = collectText(child, nextContext).trim();
      if (!content) return;
      if (tag === 'OL') {
        items.push(`${index}. ${content}`);
        index += 1;
      } else {
        items.push(`• ${content}`);
      }
    });
    return items.length ? `${items.join('\n')}\n\n` : '';
  }

  if (tag === 'LI') {
    return Array.from(node.childNodes).map((child) => collectText(child, nextContext)).join(' ');
  }

  const content = Array.from(node.childNodes).map((child) => collectText(child, nextContext)).join('');

  if (tag === 'P' || tag === 'DIV') {
    return content ? `${content.trim()}\n\n` : '';
  }

  return content;
};

export const richTextToWhatsApp = (input = '') => {
  const html = sanitizeRichText(input);
  if (!html) return '';

  if (typeof document === 'undefined') {
    return html
      .replace(/<\/?p>/gi, '\n')
      .replace(/<br\s*\/?\s*>/gi, '\n')
      .replace(/<li>/gi, '• ')
      .replace(/<\/li>/gi, '\n')
      .replace(/<\/?ul>/gi, '')
      .replace(/<\/?ol>/gi, '')
      .replace(/<[^>]+>/g, '')
      .replace(/\s+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  const temp = document.createElement('div');
  temp.innerHTML = html;
  const text = Array.from(temp.childNodes).map((node) => collectText(node)).join('');
  return text.replace(/\n{3,}/g, '\n\n').trim();
};

export const normalizeRichTextSource = (input = '') => whatsappToRichText(input);

export const normalizeWhatsappText = (input = '') => richTextToWhatsApp(whatsappToRichText(input));

export const stripRichText = (input = '') => {
  const html = whatsappToRichText(input);
  if (!html) return '';

  if (typeof document === 'undefined') {
    return html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  }

  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent.replace(/\s+/g, ' ').trim();
};

export const isRichTextEmpty = (input = '') => stripRichText(input).length === 0;
